import { ThreeEvent, useThree } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import * as THREE from "three";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { createAreaFillFromPoint, getAreaFillCandidateAtPoint, isPointInsideExistingAreaFill } from "../../utils/areaFillGeometry";
import { projectToWorld } from "../FloorPlan/projectToWorld";

const AreaBleedFillPlane = observer(() => {
  const { gl, camera } = useThree();
  const [previewVertices, setPreviewVertices] = useState<THREE.Vector2[]>([]);

  useEffect(() => {
    if (!editorStore.areaBleedFillMode) {
      setPreviewVertices([]);
      document.body.style.cursor = "auto";
    }
  }, [editorStore.areaBleedFillMode]);

  if (!editorStore.areaBleedFillMode || editorStore.view3D) {
    return null;
  }

  const getPointerPosition = (event: ThreeEvent<PointerEvent>) => {
    const [x, y] = projectToWorld(event.clientX, event.clientY, gl, camera);
    return new THREE.Vector2(x, y);
  };

  const updatePreview = (event: ThreeEvent<PointerEvent>) => {
    const point = getPointerPosition(event);

    if (isPointInsideExistingAreaFill(floorplannerStore, point)) {
      setPreviewVertices([]);
      document.body.style.cursor = "copy";
      return;
    }

    const candidate = getAreaFillCandidateAtPoint(floorplannerStore, point);
    setPreviewVertices(candidate?.vertices ?? []);
    document.body.style.cursor = candidate ? "copy" : "not-allowed";
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    const point = getPointerPosition(event);
    const areaFillId = createAreaFillFromPoint(floorplannerStore, point);
    if (!areaFillId) return;

    const areaFill = floorplannerStore.symbolsMap.get(areaFillId);
    if (!areaFill) return;

    editorStore.clearSelections();
    editorStore.addSelection(areaFill);
    floorplannerStore.updateSymbolProperty(areaFill.id, "selected", true);
    editorStore.requestOpenFillProperties();
    editorStore.setAreaBleedFillMode(false);
    setPreviewVertices([]);
  };

  return (
    <group
      onPointerDown={handlePointerDown}
      onPointerMove={updatePreview}
      onPointerLeave={() => {
        setPreviewVertices([]);
        document.body.style.cursor = "auto";
      }}
    >
      {previewVertices.length >= 3 && (
        <>
          <mesh position={[0, 0, 0.23]}>
            <shapeGeometry args={[new THREE.Shape(previewVertices)]} />
            <meshBasicMaterial color="#2563eb" transparent opacity={0.18} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
          <Line
            points={previewVertices.map((vertex) => [vertex.x, vertex.y, 0.24])}
            color="#2563eb"
            lineWidth={1.2}
            dashed
            dashSize={0.16}
            gapSize={0.08}
          />
        </>
      )}
      <mesh position={[0, 0, 0.22]} userData={{ id: "area-bleed-fill-plane" }}>
        <planeGeometry args={[1000, 1000]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
});

export default AreaBleedFillPlane;
